import { useEffect, useMemo, useRef } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import { FastField } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Colors } from "../../../utils/style/colors";
import { selectV4Scenarios } from "../../../redux/selectors/scenario.selector";
import { fetchV4Scenarios } from "../../../redux/actions/scenario.actions";
import { scenarioTriggers } from "../../../utils/models/scenarios/scenarioTriggers";
import { humanizeSettingKey } from "../utils";

// Scenario (uuid) field as a dropdown of the account's quick-button scenarios.
// V4 DevCon supports only V4 scenarios; the stored value is the scenario id.
export default function ScenarioInput({ id, name, onChange, disabled }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const scenarios = useSelector(selectV4Scenarios());

  // DevCon doesn't load scenarios itself — fetch the V4 set once on mount.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      dispatch(fetchV4Scenarios());
    }
  }, [dispatch]);

  const options = useMemo(() => {
    // v4Scenarios is an Immutable List — flatten so the mapped <MenuItem>s render.
    const list = scenarios?.toArray ? scenarios.toArray() : scenarios || [];
    return list
      .filter(
        (s) =>
          s &&
          s.get("enabled") &&
          s.get("trigger") === scenarioTriggers.quickButton
      )
      .map((s) => ({ id: s.id, name: s.get("name") }));
  }, [scenarios]);

  return (
    <FastField name={id}>
      {({ field, form }) => {
        const value = field.value ?? "";
        const isKnown =
          !value ||
          options.some((o) => String(o.id) === String(value));

        return (
          <Box
            sx={{
              py: 0.75,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <InputLabel
              id={`${id}-label`}
              sx={{
                flex: 1,
                position: "relative",
                transform: "none",
                color: Colors.black,
                fontSize: "0.95rem",
              }}
            >
              {t(name, { defaultValue: humanizeSettingKey(name) })}
            </InputLabel>
            <FormControl
              size="small"
              sx={{ width: "fit-content", maxWidth: "60%", minWidth: "160px" }}
              disabled={disabled}
            >
              <Select
                labelId={`${id}-label`}
                value={value}
                displayEmpty
                onChange={(e) => {
                  form.setFieldValue(field.name, e.target.value);
                  onChange?.(field.name, e.target.value);
                }}
              >
                <MenuItem value="">
                  <em>{t("none", { defaultValue: "None" })}</em>
                </MenuItem>
                {options.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
                {/* Keep an unlisted current value selectable so it isn't lost
                    (not yet loaded, disabled, or non-quick-button). */}
                {!isKnown && <MenuItem value={value}>{value}</MenuItem>}
              </Select>
            </FormControl>
          </Box>
        );
      }}
    </FastField>
  );
}
